import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    FiArrowLeft,
    FiCalendar,
    FiClock,
    FiDownload,
    FiEye,
    FiFileText,
    FiFolder,
    FiInbox,
    FiInfo,
    FiMail,
    FiMapPin,
    FiMessageSquare,
    FiPaperclip,
    FiPhone,
    FiTrendingUp,
    FiUploadCloud,
    FiUser,
    FiX,
} from 'react-icons/fi';
import DOMPurify from 'dompurify';
import { FileText, Loader } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import FileViewer from '../common/FileViewer';
import Toast from '../common/Toast';
import { getSingleAssignment } from '../../lib/supabase/single-assignment';
import {
    getCustomerConversationCustomerStats,
    getCustomerConversations,
    subscribeToCustomerConversations,
} from '../../lib/supabase/customerConversations';
import DropdownSection from '../common/DropdownSection';
import BackButton from '../common/BackButton';

const TYPE_LABELS = {
    booked_dates_email: 'Booked dates',
    job_files_email: 'Job files',
};

const FILE_TYPE_LABELS = {
    initial: 'Job Files',
    design: 'Design Files',
    estimation: 'Estimation Files',
    delivery: 'Delivery Files',
    timber: 'Timber Files',
    custom: 'Custom Files',
    other: 'Other Files',
};

const STATUS_TONE = {
    sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
};

const DIRECTION_TONE = {
    outbound: 'bg-slate-900 text-white',
    inbound: 'bg-white text-slate-700 border border-slate-200',
};

const normalizeConversationFiles = (files) => {
    if (!files) return [];

    const parsed = typeof files === 'string'
        ? (() => {
            try {
                return JSON.parse(files);
            } catch {
                return [];
            }
        })()
        : files;

    if (!Array.isArray(parsed)) return [];

    return parsed
        .filter(Boolean)
        .map((file, index) => ({
            ...file,
            id: file.id || `${file.filename || file.url || 'file'}-${index}`,
            filename: file.filename || file.url,
            original_name: file.original_name || file.filename || file.url || 'Attachment',
            uploaded_at: file.uploaded_at || file.created_at || null,
        }))
        .filter(file => !!file.filename);
};

// const renderConversationBody = (conversation) => {
//     const isInboundEmail =
//         conversation.direction === 'inbound' &&
//         conversation.provider === 'resend' &&
//         conversation.html_content;

//     if (true) {
//         const safeHtml = DOMPurify.sanitize(conversation.html_content, {
//             ADD_ATTR: ['target', 'style', 'src', 'width', 'height', 'alt'],
//             ADD_TAGS: ['style'],
//         });

//         return (
//             <div
//                 className="email-html-content text-sm leading-6 text-slate-700"
//                 dangerouslySetInnerHTML={{ __html: safeHtml }}
//             />
//         );
//     }

//     return (
//         <p className="whitespace-pre-line text-sm leading-6 text-slate-700">
//             {conversation.content || 'No plain-text content stored for this event.'}
//         </p>
//     );
// };

const getFilePath = (file, companySlug, jobId) => {
    const candidate = file?.filename || file?.url || '';

    if (!candidate) return null;

    if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
        const parts = candidate.split('/storage/v1/object/public/job-files/');
        return parts.length > 1 ? decodeURIComponent(parts[1]) : null;
    }

    if (candidate.includes(`${companySlug}/${jobId}`) || candidate.startsWith(`${companySlug}/`)) {
        return candidate;
    }

    return `${companySlug}/${jobId}/${candidate}`;
};

const formatDateTime = (value) => {
    if (!value) return 'Unknown';
    return new Date(value).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatRelative = (value) => {
    if (!value) return 'No activity yet';

    const diffMs = Date.now() - new Date(value).getTime();
    const minutes = Math.round(diffMs / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;

    return formatDateTime(value);
};

const getDirectionLabel = (direction) => direction === 'inbound' ? 'Customer' : 'Your team';
const getTypeLabel = (type) => TYPE_LABELS[type] || String(type || 'Conversation').replace(/_/g, ' ');
const truncateText = (value, max = 90) => {
    if (!value) return 'No content preview available';
    const singleLine = String(value).replace(/\s+/g, ' ').trim();
    if (singleLine.length <= max) return singleLine;
    return `${singleLine.slice(0, max).trim()}...`;
};

const DISPLAY_OPTIONS = [
    { key: 'html', label: 'Formatted' },
    { key: 'text', label: 'Text' },
    { key: 'raw', label: 'Original' },
];

const getStoredDisplayMode = () => {
    return localStorage.getItem('customer_conversation_display_mode') || 'html';
};

const renderConversationBody = (conversation, displayMode) => {
    const metadata = conversation.metadata || {};

    const htmlValue =
        conversation.html_content ||
        metadata.reply_html ||
        null;

    const textValue =
        conversation.content ||
        metadata.reply_text ||
        null;

    const rawValue =
        metadata.raw_html ||
        metadata.raw_text ||
        conversation.html_content ||
        conversation.content ||
        null;

    if (displayMode === 'html') {
        if (!htmlValue) {
            return (
                <p className="whitespace-pre-line text-sm leading-6 text-slate-700">
                    {textValue || 'No formatted content available.'}
                </p>
            );
        }

        const safeHtml = DOMPurify.sanitize(htmlValue, {
            ADD_ATTR: ['target', 'style', 'src', 'width', 'height', 'alt'],
            ADD_TAGS: ['style'],
        });

        return (
            <div
                className="email-html-content text-sm leading-6 text-slate-700"
                dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
        );
    }

    if (displayMode === 'raw') {
        const rawIsHtml = String(rawValue || '').includes('<');

        if (rawIsHtml) {
            const safeHtml = DOMPurify.sanitize(rawValue, {
                ADD_ATTR: ['target', 'style', 'src', 'width', 'height', 'alt'],
                ADD_TAGS: ['style'],
            });

            return (
                <div
                    className="email-html-content text-sm leading-6 text-slate-700"
                    dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
            );
        }

        return (
            <p className="whitespace-pre-line text-sm leading-6 text-slate-700">
                {rawValue || 'No original content available.'}
            </p>
        );
    }

    return (
        <p className="whitespace-pre-line text-sm leading-6 text-slate-700">
            {textValue || 'No plain-text content stored for this event.'}
        </p>
    );
};

const AssignmentConversation = ({
    mode = 'page',
    assignment: assignmentProp = null,
    company: companyProp = null,
    isOpen = false,
    onClose,
}) => {
    const params = useParams();
    const navigate = useNavigate();
    const auth = useAuth();

    const company = companyProp || auth.company;
    const companySlug = params.companySlug || company?.slug;
    const routeJobId = params.jobId;
    const effectiveJobId = assignmentProp?.id || routeJobId;

    const [assignment, setAssignment] = useState(assignmentProp || null);
    const [loading, setLoading] = useState(!assignmentProp);
    const [conversations, setConversations] = useState([]);
    const [stats, setStats] = useState({ totalJobs: 0, activeJobs: 0, completedJobs: 0 });
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [viewerFiles, setViewerFiles] = useState([]);
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);

    const [displayMode, setDisplayMode] = useState(getStoredDisplayMode);

    useEffect(() => {
        setAssignment(assignmentProp || null);
        setLoading(!assignmentProp);
    }, [assignmentProp]);

    useEffect(() => {
        if (mode !== 'modal' || !isOpen) return;

        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = original;
        };
    }, [mode, isOpen]);

    useEffect(() => {
        if (assignmentProp || !company?.id || !effectiveJobId) return;

        let active = true;

        const loadAssignment = async () => {
            try {
                setLoading(true);
                const data = await getSingleAssignment(effectiveJobId, company.id);
                if (active) setAssignment(data);
            } catch (error) {
                console.error('Error loading assignment conversation page:', error);
                if (active) Toast.error('Failed to load assignment conversation');
            } finally {
                if (active) setLoading(false);
            }
        };

        loadAssignment();

        return () => {
            active = false;
        };
    }, [assignmentProp, company?.id, effectiveJobId]);

    useEffect(() => {
        if (!company?.id || !assignment?.id) return;

        let active = true;

        const loadConversationData = async () => {
            try {
                const [conversationRows, customerStats] = await Promise.all([
                    getCustomerConversations(company.id, assignment.id),
                    getCustomerConversationCustomerStats(company.id, assignment.customer_name),
                ]);

                if (!active) return;
                setConversations(conversationRows);
                setStats(customerStats);
            } catch (error) {
                console.error('Error loading customer conversations:', error);
                if (active) Toast.error('Failed to load customer conversation history');
            }
        };

        loadConversationData();

        const channel = subscribeToCustomerConversations(company.id, assignment.id, () => {
            loadConversationData();
        });

        return () => {
            active = false;
            supabase.removeChannel(channel);
        };
    }, [company?.id, assignment?.id, assignment?.customer_name]);

    const handleDisplayModeChange = (mode) => {
        setDisplayMode(mode);
        localStorage.setItem('customer_conversation_display_mode', mode);
    };

    const assignmentFiles = useMemo(() => {
        return (assignment?.job_files || []).filter(file => file?.filename && file.filename !== '__folder_placeholder__');
    }, [assignment?.job_files]);

    const conversationFiles = useMemo(() => {
        return conversations.flatMap((conversation) =>
            normalizeConversationFiles(conversation.files).map((file) => ({
                ...file,
                conversationId: conversation.id,
                created_at: conversation.created_at,
                direction: conversation.direction,
            }))
        );
    }, [conversations]);

    const latestConversationAt = conversations[0]?.created_at || null;
    const inboundCount = conversations.filter(item => item.direction === 'inbound').length;
    const outboundCount = conversations.filter(item => item.direction !== 'inbound').length;
    const customerUploads = conversationFiles.filter(file => file.direction === 'inbound');

    const categorizedAssignmentFiles = useMemo(() => {
        return assignmentFiles.reduce((acc, file) => {
            const key = file.file_type || 'other';
            if (!acc[key]) acc[key] = [];
            acc[key].push(file);
            return acc;
        }, {});
    }, [assignmentFiles]);

    const assignmentFileSections = useMemo(() => {
        const orderedKeys = ['initial', 'design', 'estimation', 'timber', 'delivery', 'custom', 'other'];
        return orderedKeys
            .filter(key => categorizedAssignmentFiles[key])
            .map(key => ({
                key,
                title: FILE_TYPE_LABELS[key] || key,
                files: categorizedAssignmentFiles[key],
            }));
    }, [categorizedAssignmentFiles]);

    const topStats = [
        {
            label: 'Touchpoints',
            value: conversations.length,
            hint: `${inboundCount} inbound / ${outboundCount} outbound`,
            icon: FiMessageSquare,
        },
        {
            label: 'Customer Jobs',
            value: stats.totalJobs,
            hint: `${stats.activeJobs} active right now`,
            icon: FiTrendingUp,
        },
        {
            label: 'Files Shared',
            value: assignmentFiles.length + conversationFiles.length,
            hint: `${conversationFiles.length} came from conversation`,
            icon: FiPaperclip,
        },
        {
            label: 'Last Contact',
            value: formatRelative(latestConversationAt),
            hint: latestConversationAt ? formatDateTime(latestConversationAt) : 'No messages sent yet',
            icon: FiClock,
        },
    ];

    const openFileViewer = (file, allFiles) => {
        const files = allFiles.length > 0 ? allFiles : [file];
        setSelectedFile(file);
        setViewerFiles(files);
        setSelectedFileIndex(Math.max(files.findIndex(item => (item.id || item.filename) === (file.id || file.filename)), 0));
        setFileViewerOpen(true);
    };

    const downloadFile = async (file) => {
        const filePath = getFilePath(file, companySlug || company?.slug, assignment?.id);
        if (!filePath) {
            Toast.error('File path is missing');
            return;
        }

        try {
            const { data, error } = await supabase.storage.from('job-files').download(filePath);
            if (error) throw error;

            const blobUrl = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.original_name || file.filename || 'attachment';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error downloading conversation file:', error);
            Toast.error(error.message || 'Failed to download file');
        }
    };

    const renderFileRow = (file, allFiles, meta) => (
        <div key={file.id || `${file.filename}-${meta}`} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex items-start gap-3">
                    <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                        <FiFileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{file.original_name || file.filename}</p>
                        <p className="mt-1 truncate text-xs text-slate-500">{file.file_type || 'file'}{meta ? ` - ${meta}` : ''}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => openFileViewer(file, allFiles)}
                        className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                        title="View file"
                    >
                        <FiEye className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => downloadFile(file)}
                        className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                        title="Download file"
                    >
                        <FiDownload className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderEmptyFileState = (message) => (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-5 text-sm text-slate-500">
            {message}
        </div>
    );

    const content = (
        <div className={mode === 'modal' ? 'conversation-dialog mobile-fullscreen-dialog fixed inset-0 z-[10020] bg-slate-950/45 backdrop-blur-sm' : 'mobile-conversation-page w-full min-h-screen,linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)]'}>
            <div className={mode === 'modal' ? 'flex h-full items-end justify-center p-0 sm:items-center sm:p-5' : 'mx-auto w-full'}>
                <div className={mode === 'modal' ? 'conversation-dialog-panel flex h-[100dvh] w-full flex-col overflow-hidden bg-slate-50 shadow-2xl sm:h-[92vh] sm:max-w-7xl sm:rounded-[2rem]' : 'overflow-hidden rounded-[2rem] border border-white/60 bg-slate-50 shadow-[0_24px_80px_rgba(15,23,42,0.10)]'}>
                    <div className="conversation-header border-b border-slate-200 bg-[linear-gradient(135deg,#000412_0%,#19212E_58%,#474747_100%)] px-4 py-4 text-white sm:px-6 lg:px-8">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">
                                    <span>Customer Conversation</span>
                                    <span className="rounded-full border border-white/15 px-2.5 py-1 text-[11px] tracking-[0.18em] text-white/90">{assignment?.unique_id || 'Loading'}</span>
                                    <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-xs font-semibold text-emerald-200">{assignment?.job_type || 'Assignment'}</span>
                                </div>
                                <div className="mt-2 flex flex-col items-start gap-x-4 gap-y-2">
                                    <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{assignment?.customer_name || 'Customer overview'}</h1>
                                    <div className="flex flex-col items-start gap-3 text-xs text-slate-300 sm:text-sm">
                                        <div className='flex flex-wrap gap-3'>
                                            <span className="inline-flex items-center gap-1.5"><FiMail className="h-3.5 w-3.5" />{assignment?.customer_mail || assignment?.customer_email || 'No email recorded'}</span>
                                            <span className="inline-flex items-center gap-1.5"><FiPhone className="h-3.5 w-3.5" />{assignment?.customer_number || 'No phone recorded'}</span>
                                            <span className="inline-flex items-center gap-1.5"><FiMapPin className="h-3.5 w-3.5" />{truncateText(assignment?.customer_address || 'No site address recorded', 48)}</span>
                                        </div>
                                        <div className='flex flex-wrap gap-3'>
                                            <span className="inline-flex items-center gap-1.5"><FiUser className="h-3.5 w-3.5" />{assignment?.carpenter_supervisor_name || 'No supervisor'}</span>
                                            <span className="inline-flex items-center gap-1.5"><FiPhone className="h-3.5 w-3.5" />{assignment?.carpenter_supervisor_number || 'No supervisor phone'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                {mode === 'page' ? (
                                    <>
                                        <BackButton className='inline-flex items-center gap-2 !rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15' />
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/${companySlug}/assignment/${assignment?.id || effectiveJobId}`)}
                                            className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
                                        >
                                            <FileText className="h-4 w-4" />
                                            Go to Job
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to={`/${companySlug}/assignment/${assignment?.id || effectiveJobId}/conversation`}
                                            className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/15"
                                        >
                                            <FiInbox className="h-4 w-4" />
                                            Open page
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
                                        >
                                            <FiX className="h-4 w-4" />
                                            Close
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="conversation-stats mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                            {topStats.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <div key={item.label} className="rounded-2xl border border-white/10 bg-slate-950/20 px-3 py-2.5">
                                        <div className="flex items-center gap-2 text-slate-300">
                                            <Icon className="h-4 w-4" />
                                            <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">{item.label}</span>
                                        </div>
                                        <div className="mt-2 flex items-end justify-between gap-2">
                                            <p className="text-lg font-semibold text-white">{item.value}</p>
                                            <p className="text-right text-[11px] leading-4 text-slate-300">{item.hint}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="conversation-body grid min-h-0 flex-1 gap-5 p-4 sm:p-6 lg:grid-cols-[1.2fr_0.8fr] lg:p-8">
                        <section className="flex min-h-0 flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
                            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                                <div>
                                    <h2 className="text-base font-bold text-slate-900">Conversation Tracker</h2>
                                    <p className="mt-1 text-sm text-slate-500">Every touchpoint with the customer on this assignment.</p>
                                </div>
                                <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{conversations.length} events</div>
                            </div>
                            <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    {DISPLAY_OPTIONS.map(option => {
                                        const active = displayMode === option.key;

                                        return (
                                            <button
                                                key={option.key}
                                                type="button"
                                                onClick={() => handleDisplayModeChange(option.key)}
                                                className={`rounded-xl px-3 py-2 text-xs font-bold transition ${active
                                                    ? 'bg-blue-600 text-white shadow-sm'
                                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-1 text-xs leading-5 text-amber-800 flex flex-row w-fit">
                                    <FiInfo className='w-3 h-3 self-center mr-1' />
                                    If the message content does not look correct, try changing the display option above.
                                </div>
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5">
                                {loading ? (
                                    <div className="flex min-h-[18rem] items-center justify-center text-slate-500">
                                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                                        Loading conversation...
                                    </div>
                                ) : conversations.length === 0 ? (
                                    <div className="flex min-h-[18rem] flex-col items-center justify-center rounded-[1.5rem] border border-dashed border-slate-200 bg-slate-50 px-6 text-center">
                                        <FiMessageSquare className="h-8 w-8 text-slate-400" />
                                        <h3 className="mt-4 text-base font-bold text-slate-900">No customer conversation yet</h3>
                                        <p className="mt-2 max-w-md text-sm text-slate-500">Once files, booked dates, or replies start flowing, the thread will appear here automatically.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {conversations.map((conversation) => {
                                            const files = normalizeConversationFiles(conversation.files);
                                            const isInbound = conversation.direction === 'inbound';
                                            const preview = truncateText(conversation.content || conversation.subject);
                                            const DropdownIcon = isInbound ? FiInbox : FiMail;

                                            const titleLabel = isInbound ? 'Customer message' : 'Team email';
                                            const titleBadge = isInbound ? 'Inbound' : 'Outbound';
                                            const titleBadgeClass = isInbound
                                                ? 'bg-sky-100 text-sky-700 border-sky-200'
                                                : 'bg-slate-900 text-white border-slate-900';

                                            const titleNode = (
                                                <div className="flex min-w-0 flex-col gap-1">
                                                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${titleBadgeClass}`}>
                                                            {titleBadge}
                                                        </span>

                                                        <span className="truncate text-sm font-semibold text-slate-900">
                                                            {titleLabel}
                                                        </span>

                                                        <span className="hidden sm:inline text-xs font-medium text-slate-400">
                                                            •
                                                        </span>

                                                        <span className="truncate text-xs font-semibold text-slate-500">
                                                            {conversation.subject || getTypeLabel(conversation.conversation_type)}
                                                        </span>
                                                    </div>

                                                    <div className="flex min-w-0 flex-wrap justify-between gap-x-3 gap-y-1 text-xs text-slate-500">
                                                        <div className='gap-4 items-center flex flex-row'>
                                                            <span>
                                                                {isInbound
                                                                    ? `From: ${conversation.from_email || 'Customer'}`
                                                                    : `Sent by: ${conversation.sent_by_name || 'Your team'}`}
                                                            </span>

                                                            {files.length > 0 && (
                                                                <span className="inline-flex items-center gap-1 font-semibold text-slate-600">
                                                                    <FiPaperclip className="h-3.5 w-3.5" />
                                                                    {files.length} file{files.length !== 1 ? 's' : ''}
                                                                </span>
                                                            )}
                                                        </div>

                                                        <span className='!self-end !content-end !items-end'>
                                                            {formatRelative(conversation.created_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );

                                            return (
                                                <DropdownSection
                                                    key={conversation.id}
                                                    title={titleNode}
                                                    icon={DropdownIcon}
                                                    iconStyle={isInbound ? 'text-sky-600' : 'text-slate-700'}
                                                    titleStyle="min-w-0 flex-1"
                                                    subtextStyle="text-[11px] font-semibold text-slate-500 "
                                                    defaultOpen={false}
                                                    customMainStyle={`!rounded-[1rem] !border-slate-200 !shadow-sm ${isInbound ? '!border-l-4 !border-l-sky-500' : '!border-l-4 !border-l-slate-800'
                                                        }`}
                                                    customClickableStyle={`!px-4 !py-3 ${isInbound
                                                        ? '!bg-[linear-gradient(180deg,#ffffff_0%,#f0f9ff_100%)]'
                                                        : '!bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]'
                                                        }`}
                                                    customContainerStyle="!p-4 !bg-white"
                                                >
                                                    <div className="space-y-4">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${DIRECTION_TONE[conversation.direction] || DIRECTION_TONE.outbound}`}>
                                                                {getDirectionLabel(conversation.direction)}
                                                            </span>
                                                            <span className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">{getTypeLabel(conversation.conversation_type)}</span>
                                                            <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_TONE[conversation.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                                                {conversation.status || 'sent'}
                                                            </span>
                                                        </div>

                                                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                                            <span className="inline-flex items-center gap-1"><FiCalendar className="h-3.5 w-3.5" />{formatDateTime(conversation.created_at)}</span>
                                                            {conversation.sent_by_name && <span className="inline-flex items-center gap-1"><FiUser className="h-3.5 w-3.5" />{conversation.sent_by_name}</span>}
                                                            {conversation.to_email?.length > 0 && <span className="truncate">To: {conversation.to_email.join(', ')}</span>}
                                                            {conversation.cc_emails?.length > 0 && <span className="truncate">Cc: {conversation.cc_emails.join(', ')}</span>}
                                                        </div>

                                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 overflow-x-auto">
                                                            {renderConversationBody(conversation, displayMode)}
                                                        </div>

                                                        {files.length > 0 && (
                                                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                                                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800">
                                                                    <FiPaperclip className="h-4 w-4" />
                                                                    Attachments
                                                                </div>
                                                                <div className="space-y-2">
                                                                    {files.map(file => renderFileRow(file, files, formatRelative(conversation.created_at)))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </DropdownSection>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </section>

                        <section className="flex min-h-0 flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
                            <div className="border-b border-slate-200 px-5 py-4">
                                <h2 className="text-base font-bold text-slate-900">Assignment Files</h2>
                                <p className="mt-1 text-sm text-slate-500">Production files on the job plus anything the customer added through the conversation flow.</p>
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5">
                                <div className="space-y-3">
                                    <DropdownSection
                                        title="Customer Uploads"
                                        subtext={`${customerUploads.length} file${customerUploads.length !== 1 ? 's' : ''}`}
                                        icon={FiUploadCloud}
                                        iconStyle="text-sky-700"
                                        titleStyle="text-sm text-slate-900"
                                        subtextStyle="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"
                                        defaultOpen={true}
                                        customMainStyle="!rounded-[1.5rem] !border-slate-200"
                                        customClickableStyle="!px-4 !py-3.5 !bg-slate-50 hover:!bg-slate-100"
                                        customContainerStyle="!p-4 !bg-white"
                                    >
                                        <div className="space-y-2">
                                            {customerUploads.length === 0
                                                ? renderEmptyFileState('No customer-uploaded conversation files yet.')
                                                : customerUploads.map(file => renderFileRow(file, customerUploads, formatRelative(file.created_at)))}
                                        </div>
                                    </DropdownSection>

                                    {assignmentFileSections.length === 0 ? (
                                        renderEmptyFileState('No assignment files uploaded yet.')
                                    ) : (
                                        assignmentFileSections.map((section) => (
                                            <DropdownSection
                                                key={section.key}
                                                title={section.title}
                                                subtext={`${section.files.length} file${section.files.length !== 1 ? 's' : ''}`}
                                                icon={FiFolder}
                                                iconStyle="text-slate-700"
                                                titleStyle="text-sm text-slate-900"
                                                subtextStyle="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"
                                                defaultOpen={section.key === 'initial'}
                                                customMainStyle="!rounded-[1.5rem] !border-slate-200"
                                                customClickableStyle="!px-4 !py-3.5 !bg-slate-50 hover:!bg-slate-100"
                                                customContainerStyle="!p-4 !bg-white"
                                            >
                                                <div className="space-y-2">
                                                    {section.files.map(file =>
                                                        renderFileRow(
                                                            file,
                                                            section.files,
                                                            file.quote_type ? `${file.file_type || 'file'} - ${file.quote_type}` : file.file_type
                                                        )
                                                    )}
                                                </div>
                                            </DropdownSection>
                                        ))
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {fileViewerOpen && selectedFile && (
                <FileViewer
                    onClose={() => setFileViewerOpen(false)}
                    file={selectedFile}
                    filename={selectedFile.filename}
                    originalName={selectedFile.original_name}
                    files={viewerFiles}
                    currentIndex={selectedFileIndex}
                    onNavigate={(nextIndex) => {
                        setSelectedFileIndex(nextIndex);
                        setSelectedFile(viewerFiles[nextIndex]);
                    }}
                    company={companySlug || company?.slug}
                    jobId={assignment?.id}
                    fileType={selectedFile.file_type || 'initial'}
                />
            )}
        </div>
    );

    if (mode === 'modal') {
        if (!isOpen) return null;
        return createPortal(content, document.body);
    }

    return content;
};

export default AssignmentConversation;
